import type { ProofRequestItem } from "@/components/marketing/proof/proof-request-types";
import {
  ShieldCheck,
  Lock,
  KeyRound,
  Boxes,
  FileSignature,
  ScrollText,
  Clock,
  Server,
  Database,
  Brain,
} from "@/lib/public-icons";
import { SECURITY_FAQS, type MarketingFaq } from "@alm/domain/marketing/faqs";

export const adminDeskImg = "/marketing/admin_desk.jpg";

/* Stand-ins for a third-party attestation, as things a reader can ask for. Each is already named on
 * this page or in the shared FAQs, so none is a new promise. */
export const evidenceItems: ProofRequestItem[] = [
  {
    id: "baa",
    label: "The Business Associate Agreement",
    detail:
      "The BAA accepted with every order, before any resident information is entered. Read it now, or have a copy sent.",
    href: "/baa",
  },
  {
    id: "overview",
    label: "The security overview",
    detail:
      "The controls on this page written up as one document, for whoever reviews vendors on your behalf.",
  },
  {
    id: "exit",
    label: "Data export and exit terms",
    detail: "What exports, in which formats, and what happens to your records if you ever leave.",
  },
  {
    id: "reference",
    label: "An introduction to a live community",
    detail: "An operator running PEOPLE AI Workforce in production today, who will take your call.",
  },
];

export const principles = [
  {
    icon: KeyRound,
    title: "Authenticated access",
    description:
      "JWT-based authentication with bcrypt-hashed credentials. Inactive accounts are blocked at login.",
  },
  {
    icon: Lock,
    title: "Role-based access control",
    description:
      "Granular roles (Super Admin, Staff) scope every screen and API to exactly what a user should see.",
  },
  {
    icon: Boxes,
    title: "Per-tenant data isolation",
    description:
      "Each organization only ever sees its own communities and residents, enforced at the application layer.",
  },
  {
    icon: FileSignature,
    title: "Compliant e-signatures",
    description:
      "ESIGN/UETA-aligned document signing with SHA-256 hashing, signature certificates and audit logging.",
  },
  {
    icon: ScrollText,
    title: "Audit trails",
    description:
      "Signed clinical notes are immutable; edits and re-signs are recorded with a full audit history.",
  },
  {
    icon: Clock,
    title: "Hardened by default",
    description:
      "Rate limiting, request timeouts and secure token handling protect every endpoint.",
  },
  {
    icon: Server,
    title: "Secure file storage",
    description:
      "Documents are stored in object storage and served through short-lived, presigned URLs.",
  },
  {
    icon: ShieldCheck,
    title: "Least-privilege everywhere",
    description: "Server-controlled status and signature metadata, never trusted from the client.",
  },
];

/* What changes through Aria and what deliberately does not: she is not a second identity with an
 * API key and her own reach. Each line is a control in the code: access model, query path, write
 * path, authorship stamp, memory boundary. */
export const ariaControls = [
  {
    icon: KeyRound,
    title: "No identity of its own",
    description:
      "There is no Aria account to escalate to. A question runs as the person who asked it, through the same organization and facility checks as the screens, so nothing typed into a conversation can grant access the asker does not already have.",
  },
  {
    icon: Lock,
    title: "Absent, not refused",
    description:
      "What a person is not entitled to is filtered out before the model is told anything exists, so it cannot name it or mention that it is there. A record across an organization's line answers not-found, exactly as it does everywhere else.",
  },
  {
    icon: Database,
    title: "Analysis that cannot become an export",
    description:
      "Questions nobody built a screen for are answered with read-only SQL over purpose-built views, in a transaction that cannot write, under a role with no privilege anywhere else. Tenancy and entitlement are predicates inside the views, and clinical narrative is left out of them on purpose.",
  },
  {
    icon: ShieldCheck,
    title: "The button is the write",
    description:
      "A change arrives as a card carrying its exact fields, one sentence saying how it is undone, and a signed single-use token that expires in two minutes. A person confirms, and the write runs outside the model's path. A change nobody can state a way back from is not offered at all, which is why medication administration and financial postings are not on this path.",
  },
  {
    icon: FileSignature,
    title: "Stamped as a person's work",
    description:
      "Rows written this way record how they got there and are shown as their author's: drafted by Aria, confirmed by the person who pressed the button. The audit entry cannot be skipped.",
  },
  {
    icon: Brain,
    title: "Memory you can read and delete",
    description:
      "What Aria remembers is held per person and never shared, shown line by line with the conversation each came from, and deletable one at a time. Conversation threads are never mirrored to a memory vendor, because a thread holds resident names, medications and incident narrative.",
  },
];

export const commitments = [
  "PHI protected with strict facility-level scoping",
  "Encryption in transit (TLS) across the platform",
  "Object storage access via short-lived presigned URLs",
  "Server-side validation on every write",
];

/* The shared HIPAA answer hands BAAs back to the operator, which hedges on a page that offers one.
 * This answer states the attestation status itself; the other three shared answers remain exactly
 * true. */
export const faqs: MarketingFaq[] = [
  {
    q: "Is PEOPLE AI Workforce HIPAA compliant?",
    a: "Yes. PEOPLE AI Workforce operates as a HIPAA business associate: a Business Associate Agreement is accepted with every order, before any resident information is entered; PHI is encrypted in transit and at rest, access is role-based, each organization's records stay isolated from every other, and every action lands in the audit trail. We do not hold a third-party attestation such as SOC 2 or HITRUST today, and we say so rather than imply one.",
  },
  ...SECURITY_FAQS.filter((f) => !f.q.includes("HIPAA")),
];
